import React, { useState } from 'react';
import axios from 'axios';

const emptyWholesaleForm = {
  quantity_from: '',
  quantity_to: '',
  price: ''
};

function ProductDetailsModal({ product, onClose, onProductUpdated }) {
  const [currentProduct, setCurrentProduct] = useState(product);
  const [wholesaleForm, setWholesaleForm] = useState(emptyWholesaleForm);
  const [editingWholesaleId, setEditingWholesaleId] = useState(null);
  const [saving, setSaving] = useState(false);

  if (!currentProduct) {
    return null;
  }

  const refreshProduct = async () => {
    const response = await axios.get(`/api/products/${currentProduct.id}`);
    setCurrentProduct(response.data);
    if (onProductUpdated) {
      onProductUpdated(response.data);
    }
  };

  const resetWholesaleForm = () => {
    setWholesaleForm(emptyWholesaleForm);
    setEditingWholesaleId(null);
  };

  const handleWholesaleChange = (e) => {
    const { name, value } = e.target;
    setWholesaleForm(prev => ({ ...prev, [name]: value }));
  };

  const buildWholesalePayload = () => ({
    product_id: currentProduct.id,
    quantity_from: parseInt(wholesaleForm.quantity_from, 10),
    quantity_to: wholesaleForm.quantity_to === '' ? null : parseInt(wholesaleForm.quantity_to, 10),
    price: parseInt(wholesaleForm.price, 10)
  });

  const handleWholesaleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);
      const payload = buildWholesalePayload();

      if (editingWholesaleId) {
        await axios.put(`/api/wholesale-prices/${editingWholesaleId}`, {
          quantity_from: payload.quantity_from,
          quantity_to: payload.quantity_to,
          price: payload.price
        });
      } else {
        await axios.post('/api/wholesale-prices', payload);
      }

      resetWholesaleForm();
      await refreshProduct();
    } catch (error) {
      console.error('Error saving wholesale price:', error);
      alert(error.response?.data?.error || 'Lỗi khi lưu giá bán sỉ');
    } finally {
      setSaving(false);
    }
  };

  const handleEditWholesale = (price) => {
    setEditingWholesaleId(price.id);
    setWholesaleForm({
      quantity_from: price.quantity_from,
      quantity_to: price.quantity_to ?? '',
      price: price.price
    });
  };

  const handleDeleteWholesale = async (id) => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa mức giá bán sỉ này?')) {
      return;
    }

    try {
      await axios.delete(`/api/wholesale-prices/${id}`);
      await refreshProduct();
      if (editingWholesaleId === id) {
        resetWholesaleForm();
      }
    } catch (error) {
      console.error('Error deleting wholesale price:', error);
      alert(error.response?.data?.error || 'Lỗi khi xóa giá bán sỉ');
    }
  };

  return (
    <div className="modal show">
      <div className="modal-content" style={{ maxWidth: '850px' }}>
        <div className="modal-header">
          <h3 className="modal-title">Chi tiết sản phẩm</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label>Tên sản phẩm</label>
            <div>{currentProduct.name}</div>
          </div>
          <div className="form-group">
            <label>Giá nhập</label>
            <div>{currentProduct.purchase_price.toLocaleString()} ₫</div>
          </div>
          <div className="form-group">
            <label>Giá bán lẻ</label>
            <div>{currentProduct.retail_price_70k.toLocaleString()} ₫</div>
          </div>
          <div className="form-group">
            <label>Tồn kho</label>
            <div>{currentProduct.quantity || 0} cái</div>
          </div>

          <div className="card" style={{ marginTop: '20px' }}>
            <h4 className="card-title">Giá bán sỉ theo số lượng</h4>
            {currentProduct.wholesale_prices && currentProduct.wholesale_prices.length > 0 ? (
              <table>
                <thead>
                  <tr>
                    <th>Số lượng từ</th>
                    <th>Số lượng đến</th>
                    <th>Giá bán sỉ</th>
                    <th>Hành động</th>
                  </tr>
                </thead>
                <tbody>
                  {currentProduct.wholesale_prices.map(price => (
                    <tr key={price.id}>
                      <td>{price.quantity_from}</td>
                      <td>{price.quantity_to || 'Không giới hạn'}</td>
                      <td>{price.price.toLocaleString()} ₫</td>
                      <td>
                        <button type="button" onClick={() => handleEditWholesale(price)}>Sửa</button>
                        <button
                          type="button"
                          className="danger"
                          onClick={() => handleDeleteWholesale(price.id)}
                          style={{ marginLeft: '5px' }}
                        >
                          Xóa
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="empty-state">Chưa có giá bán sỉ cho sản phẩm này</div>
            )}
          </div>

          <div className="card" style={{ marginTop: '20px' }}>
            <h4 className="card-title">{editingWholesaleId ? 'Sửa mức giá bán sỉ' : 'Thêm mức giá bán sỉ'}</h4>
            <form onSubmit={handleWholesaleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '10px', alignItems: 'end' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Số lượng từ</label>
                  <input
                    type="number"
                    name="quantity_from"
                    value={wholesaleForm.quantity_from}
                    onChange={handleWholesaleChange}
                    min="1"
                    required
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Số lượng đến</label>
                  <input
                    type="number"
                    name="quantity_to"
                    value={wholesaleForm.quantity_to}
                    onChange={handleWholesaleChange}
                    min="1"
                    placeholder="Để trống = không giới hạn"
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Giá bán sỉ</label>
                  <input
                    type="number"
                    name="price"
                    value={wholesaleForm.price}
                    onChange={handleWholesaleChange}
                    min="0"
                    required
                  />
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button type="submit" disabled={saving}>{saving ? 'Đang lưu...' : 'Lưu'}</button>
                  {editingWholesaleId && (
                    <button type="button" onClick={resetWholesaleForm} style={{ background: '#999' }}>Hủy sửa</button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductDetailsModal;
